import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, breakpoints, themeLight } from '../../theme/index';
import { nafColor } from '../../core/index';
// Table
export const Table = ({ children, maxWidth, minWidth = '340px', variant = 'horizontal', className, }) => {
    const outerRef = useRef();
    const tableRef = useRef();
    const [hasOverflowRight, setHasOverflowRight] = useState(false);
    const [hasOverflowLeft, setHasOverflowLeft] = useState(false);
    const checkOverflow = useCallback(() => {
        const outer = outerRef.current;
        if (outer.scrollLeft > 0) {
            setHasOverflowLeft(true);
        }
        else {
            setHasOverflowLeft(false);
        }
        if (outer.scrollLeft < outer.scrollWidth - outer.clientWidth) {
            setHasOverflowRight(true);
        }
        else {
            setHasOverflowRight(false);
        }
    }, []);
    useEffect(() => {
        checkOverflow();
    }, [checkOverflow]);
    useEffect(() => {
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [checkOverflow]);
    useEffect(() => {
        const outer = outerRef.current;
        outer.addEventListener('scroll', checkOverflow);
        return () => outer.removeEventListener('scroll', checkOverflow);
    }, [checkOverflow]);
    return (<Wrap maxWidth={maxWidth} className={className}>
      <Outer ref={outerRef} hasOverflowLeft={hasOverflowLeft} hasOverflowRight={hasOverflowRight}>
        <TableContainer ref={tableRef} variant={variant} minWidth={minWidth}>
          {children}
        </TableContainer>
        <OverflowIndicator left visible={hasOverflowLeft}/>
        <OverflowIndicator right visible={hasOverflowRight}/>
      </Outer>
    </Wrap>);
};
export const TableRow = ({ children, onClick }) => (<Tr onClick={onClick}>{children}</Tr>);
// TableHead
export const TableHead = ({ children }) => <THead>{children}</THead>;
// TableBody
export const TableBody = ({ children }) => <TBody>{children}</TBody>;
export const TableCell = ({ children, as = 'td', align }) => (<Td as={as} align={align}>
    {children}
  </Td>);
export default Table;
const Wrap = styled.div `
  position: relative;

  ${({ maxWidth }) => maxWidth &&
    css `
      max-width: ${maxWidth};
    `}
`;
const Outer = styled.div `
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`;
const TableContainer = styled.table `
  width: 100%;
  display: table;
  table-layout: fixed;
  border-spacing: 0;
  border-collapse: collapse;
  min-width: ${(props) => props.minWidth};
`;
const OverflowIndicator = styled.div `
  position: absolute;
  bottom: 0;
  right: 0;
  width: ${spacing.space80};
  z-index: 2;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
  height: 100%;

  ${({ left }) => left &&
    css `
      left: 0;
      background: linear-gradient(to right, #fff 1%, rgba(255, 255, 255, 0) 100%);
    `}

  ${({ right }) => right &&
    css `
      right: 0;
      background: linear-gradient(to left, #fff 1%, rgba(255, 255, 255, 0) 100%);
    `}
`;
const Tr = styled.tr `
  display: table-row;
  box-sizing: border-box;

  ${({ onClick }) => onClick &&
    css `
      &:hover {
        cursor: pointer;
        background-color: ${nafColor.signature.yellow10};
      }
    `}
`;
const THead = styled.thead `
  display: table-header-group;

  tr {
    border-bottom: 1px solid;
    border-color: ${({ theme }) => (theme.border ? theme.border.default : themeLight.border.default)};
  }
`;
const TBody = styled.tbody `
  display: table-row-group;

  tr:not(:last-child) {
    border-bottom: 1px solid;
    border-color: ${({ theme }) => (theme.border ? theme.border.subtle : themeLight.border.subtle)};
  }
`;
const Td = styled.td `
  display: table-cell;
  padding: ${spacing.space16} 0;
  text-align: ${({ align }) => align || 'left'};
  box-sizing: border-box;

  ${({ as }) => as === 'td' &&
    css `
      ${fontStyle.article.articleText};

      @media (max-width: ${breakpoints.s}) {
        ${fontStyle.bodyText.bodyText};
      }
    `}

  ${({ as }) => as === 'th' &&
    css `
      ${fontStyle.article.articleTextHeader};

      @media (max-width: ${breakpoints.s}) {
        ${fontStyle.bodyText.bodyTextHeader};
      }
    `}
`;
